<ng-template #transactionTemplate let-row>
  <div>
    @if (row.filingUniqueId) {
      <a
        class="table-link"
        [routerLink]="transactionLink(row)"
        (click)="onTransactionClicked(row)"
      >
        {{ row.filingId }}
      </a>
    } @else {
      {{ row.filingId }}
    }
  </div>
  <div class="icon-row">
    @if (row.filerNote) {
      <i
        class="fa-solid fa-message"
        role="img"
        [attr.aria-label]="'Filer note: ' + row.filerNote"
        title="{{ row.filerNote }}"
      ></i>
    }
    @if (row.judgeReviewStatus === 'InProgress') {
      <i
        class="fa-solid fa-lock"
        role="img"
        aria-label="In Progress — a judge has this open"
        title="In Progress — a judge has this open"
      ></i>
    }
  </div>
</ng-template>

<ng-template #dateTimeTemplate let-row>
  <time class="date-time" [attr.datetime]="row.authorizedDate">
    {{
      row.authorizedDate | utcToTimezoneDate: row.courtTimeZone : 'MM/DD/YYYY'
    }}
  </time>
  <time class="date-time" [attr.datetime]="row.authorizedDate">
    {{ row.authorizedDate | utcToTimezoneDate: row.courtTimeZone : 'h:mm A' }}
    {{ row.courtTimeZone }}
  </time>
</ng-template>

<ng-template #caseNumberTemplate let-row>
  @if (row.cases?.length > 0) {
    @let lead = row.cases[0];
    @let link = caseLink()(row);
    <dl class="cell-details">
      <dt class="visually-hidden">Case number</dt>
      <dd [class.note-line]="caseExtra()">
        @if (caseExtra(); as extra) {
          <ng-container
            *ngTemplateOutlet="extra; context: { $implicit: lead }"
          ></ng-container>
        }
        @if (link) {
          <a class="table-link" [routerLink]="link">
            {{ lead.number || '-' }}
          </a>
        } @else {
          {{ lead.number || '-' }}
        }
      </dd>
      <dt class="visually-hidden">Case name</dt>
      <dd>
        @if (link) {
          <a class="table-link" [routerLink]="link">
            {{ lead.name }}
          </a>
        } @else {
          {{ lead.name }}
        }
      </dd>
      @if (row.courtName) {
        <dt class="visually-hidden">Court</dt>
        <dd>{{ row.courtName }}</dd>
      }
    </dl>
  }
</ng-template>

<ng-template #authorizerTemplate let-row>
  <dl class="cell-details">
    <dt class="visually-hidden">Authorizer</dt>
    <dd class="authorizer-name">{{ row.authorizerName }}</dd>
    <dt class="visually-hidden">Organization</dt>
    <dd>
      {{ row.authorizerOrganizationName }}
    </dd>
  </dl>
</ng-template>

<ng-template #documentTypeTemplate let-row>
  @if (row.documents?.length > 0) {
    @for (doc of row.documents; track doc.documentId ?? $index) {
      <div class="doc-row">
        <mat-checkbox
          class="compact"
          [checked]="isDocumentSelected(doc)"
          (change)="onDocumentCheckboxChanged($event, doc)"
        >
          {{ doc.filingType }}
        </mat-checkbox>
      </div>
    }
  }
</ng-template>

<ng-template #documentTitleTemplate let-row>
  @if (row.documents?.length > 0) {
    @for (doc of row.documents; track doc.documentId ?? $index) {
      <dl class="doc-row cell-details">
        <dt class="visually-hidden">Document title</dt>
        <dd [class.note-line]="documentExtra()">
          @if (documentExtra(); as extra) {
            <ng-container
              *ngTemplateOutlet="extra; context: { $implicit: doc }"
            ></ng-container>
          }
          <button
            type="button"
            class="table-link doc-title"
            (click)="onDocumentTitleClicked(doc)"
          >
            {{ doc.documentTitle }}
            <span class="visually-hidden">(opens in a new window)</span>
          </button>
        </dd>
        <dt class="visually-hidden">File size</dt>
        <dd>{{ formatDocumentSize(doc.numberOfBytes) }}</dd>
      </dl>
    }
  }
</ng-template>

<ng-template #documentStatusTemplate let-row>
  <span class="doc-row">{{
    judgeReviewStatusLabel(row.judgeReviewStatus)
  }}</span>
</ng-template>

<fsx-paginators
  #paginators
  [dataName]="'Transactions'"
  [showResultsCount]="true"
  [pageSizeOptions]="pageSizeOptions"
  [initialPageSize]="pageSize()"
  [totalNumberOfItems]="sortedFilings().length"
  (pageEvent)="onPageChanged($event)"
>
  <table
    mat-table
    matSort
    class="jrl-filings-table"
    [dataSource]="pagedFilings()"
    [matSortActive]="sortBy()"
    [matSortDirection]="matSortDirection()"
    (matSortChange)="onSortChange($event)"
  >
    <!-- Status indicator (color-coded leftmost cell) -->
    <ng-container matColumnDef="__status">
      <th mat-header-cell scope="col" *matHeaderCellDef class="status-cell">
        <span class="visually-hidden">Status indicator</span>
      </th>
      <td
        mat-cell
        *matCellDef="let row"
        class="status-cell"
        [style.border-left-color]="row.fsxTableRowStatus"
      ></td>
    </ng-container>

    <!-- Selection checkbox -->
    <ng-container matColumnDef="__select">
      <th mat-header-cell scope="col" *matHeaderCellDef class="select-cell">
        <mat-checkbox
          class="compact"
          (change)="onMasterToggle()"
          [checked]="isAllSelected()"
          [indeterminate]="isAnySelected() && !isAllSelected()"
          aria-label="Select all filings"
        >
        </mat-checkbox>
      </th>
      <td mat-cell *matCellDef="let row" class="select-cell">
        <mat-checkbox
          class="compact"
          (change)="onRowToggle(row)"
          [checked]="isRowSelected(row)"
          [aria-label]="'Select filing ' + row.filingId"
        >
        </mat-checkbox>
      </td>
    </ng-container>

    <!-- Dynamic columns driven by displayTableColumns -->
    @for (col of displayTableColumns(); track col.title) {
      <ng-container [matColumnDef]="col.title">
        @if (col.sortableField) {
          <th
            mat-header-cell
            scope="col"
            *matHeaderCellDef
            [mat-sort-header]="col.sortableField"
          >
            {{ col.title }}
          </th>
        } @else {
          <th mat-header-cell scope="col" *matHeaderCellDef>{{ col.title }}</th>
        }
        <td mat-cell *matCellDef="let row">
          <ng-container
            *ngTemplateOutlet="col.content; context: { $implicit: row }"
          ></ng-container>
        </td>
      </ng-container>
    }

    <tr mat-header-row *matHeaderRowDef="displayedColumnKeys()"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumnKeys()"></tr>

    <tr *matNoDataRow>
      <td class="no-data-cell" [attr.colspan]="displayedColumnKeys().length">
        No judge review items found
      </td>
    </tr>
  </table>
</fsx-paginators>
